import DateRangePicker, {LOCK_UNAVAILABLE, LOCK_LOCKED, LOCK_OVERLAP_AVAILABLE} from 'components/daterangepicker-base';
import {dateFormat, asBoolean} from 'methods/utils';

export {
    LOCK_LOCKED,
    LOCK_UNAVAILABLE,
    LOCK_OVERLAP_AVAILABLE,
}

/**
 * Класс - обёртка вендорного компонента
 */
function DateRangePickerWrapper($element, options = {}) {
    // ссылка на экземпляр
    if ($element.instance) {
        return $element.instance;
    }

    // забронированные даты
    this._visualSelectionRestrictions = [];
    this.restrictions = [];
    this._restrictionsByDate = {};

    // наследуемся
    DateRangePicker.call(this, $element, options);

    // переданы периоды работы
    this.setRestrictions(options.restrictions);
}

// цепочка прототипов
DateRangePickerWrapper.prototype = Object.create(DateRangePicker.prototype, {
    constructor: {
        value: DateRangePickerWrapper,
        enumerable: false,
        writable: true,
    }
});

/**
 * Получение ограничения по дате
 * @param  {Date} date Дата
 * @return {Object|null} Объект с полями
 *
 * min_stay - минимальный срок проживания
 * min_stay_arrival - минимальный срок проживания при заезде в эту дату
 * max_stay - максимальный срок проживания
 * closed - закрыт
 * closed_arrival - закрыт для заезда
 * closed_departure - закрыт для выезда
 */
DateRangePickerWrapper.prototype.getRestriction = function(date) {
    if (!date || !this._restrictionsByDate) {
        return null;
    }

    return this._restrictionsByDate[dateFormat(date, 'Y-m-d')] ?? null;
}

DateRangePickerWrapper.prototype.getRestrictions = function(date_from, date_to) {
    const fromYmd = date_from ? dateFormat(date_from, 'Y-m-d') : null;
    const toYmd = date_to ? dateFormat(date_to, 'Y-m-d') : null;

    return (this.restrictions ?? []).filter(restriction => {
        const ymd = dateFormat(restriction.date, 'Y-m-d');

        return (!fromYmd || ymd >= fromYmd) && (!toYmd || ymd <= toYmd);
    });
}

/**
 * Прокидываем ограничения
 * @param {Array} restrictions Массив дат
 */
DateRangePickerWrapper.prototype.setRestrictions = function(restrictions = []) {
    if (!Array.isArray(restrictions) || !restrictions.length) {
        this.restrictions = [];
        this._restrictionsByDate = {};
        return;
    }

    this._restrictionsByDate = {};

    this.restrictions = restrictions.map(restriction => {
        const dateKey = String(restriction.date ?? '').slice(0, 10);
        restriction.date = new Date(`${dateKey}T12:00:00`);

        ['min_stay', 'min_stay_arrival', 'max_stay'].forEach(field => {
            if (typeof restriction[field] != 'undefined') {
                restriction[field] = Number(restriction[field]);
            }
        });

        ['closed', 'closed_arrival', 'closed_departure', 'zero_quota', 'no_prices'].forEach(field => {
            if (typeof restriction[field] != 'undefined') {
                restriction[field] = asBoolean(restriction[field]);
            }
        });

        this._restrictionsByDate[dateKey] = restriction;

        return restriction;
    });

    // обновление рендера
    this.update();
}

/**
 * Фильтр текста подсказки
 * @param  {Number} days Количество выбранных дней
 * @return {String}
 */
DateRangePickerWrapper.prototype._filterTooltipText = function(days) {
    if (typeof this.options.filter.tooltipText == 'function') {
        return this.options.filter.tooltipText.call(this, days) || '';
    }

    const reason = this._getLockReason(this._visualSelection.date_to);

    if (reason && reason.tooltip) {
        return reason.tooltip;
    }

    const nights = days - 1;

    if (!nights) {
        return;
    }

    return this.plural(nights, ['%d ночь', '%d ночи', '%d ночей']).replace('%d', nights);
}

/**
 * Фильтр заблокированных дат
 * @param  {Date}   date Дата
 * @return {Number}      Одна из констант LOCK_...
 */
DateRangePickerWrapper.prototype._filterLockDays = function(date) {
    const reason = this._getLockReason(date);

    if (reason && reason.status) {
        return reason.status;
    }

    return DateRangePicker.prototype._filterLockDays.call(this, date);
}

/**
 * Можно ли выбрать дату как заезд (без активного выбора диапазона)
 * @param  {Date} date
 * @return {Boolean}
 */
DateRangePickerWrapper.prototype._isArrivalSelectable = function(date) {
    if (!(date instanceof Date)) {
        return false;
    }

    const checkDate = new Date(date.getTime());
    checkDate.setHours(0, 0, 0, 0);

    return !this._filterLockDays(checkDate);
}

/**
 * Первая дата, доступная для заезда, в заданном диапазоне
 * @param  {Date} from
 * @param  {Date} to
 * @return {Date|null}
 */
DateRangePickerWrapper.prototype._findFirstAvailableArrivalDate = function(from, to) {
    if (!(from instanceof Date) || !(to instanceof Date)) {
        return null;
    }

    const day = new Date(from.getTime());
    day.setHours(0, 0, 0, 0);

    const end = new Date(to.getTime());
    end.setHours(0, 0, 0, 0);

    while (day <= end) {
        if (this._isArrivalSelectable(day)) {
            return new Date(day.getTime());
        }

        day.setDate(day.getDate() + 1);
    }

    return null;
}

/**
 * Есть ли в текущем окне календаря хотя бы один доступный заезд
 * @return {Boolean}
 */
DateRangePickerWrapper.prototype._visibleMonthsHaveAvailableArrival = function() {
    if (!this._selectedDate) {
        return true;
    }

    const monthStart = new Date(this._selectedDate.getTime());
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);

    const lastVisibleMonth = new Date(monthStart.getTime());
    lastVisibleMonth.setMonth(lastVisibleMonth.getMonth() + this.options.monthsCount - 1);

    const viewEnd = new Date(lastVisibleMonth.getFullYear(), lastVisibleMonth.getMonth() + 1, 0);
    viewEnd.setHours(0, 0, 0, 0);

    let rangeStart = monthStart;
    if (rangeStart < this.options.minDate) {
        rangeStart = new Date(this.options.minDate.getTime());
        rangeStart.setHours(0, 0, 0, 0);
    }

    let rangeEnd = viewEnd;
    if (this.options.maxDate && rangeEnd > this.options.maxDate) {
        rangeEnd = new Date(this.options.maxDate.getTime());
        rangeEnd.setHours(0, 0, 0, 0);
    }

    if (rangeStart > rangeEnd) {
        return false;
    }

    return Boolean(this._findFirstAvailableArrivalDate(rangeStart, rangeEnd));
}

/**
 * При открытии показываем месяц с выбранными датами или первый доступный заезд
 */
DateRangePickerWrapper.prototype.scrollToRelevantMonthOnOpen = function() {
    if (this._selection.date_from instanceof Date) {
        const month = new Date(this._selection.date_from.getTime());
        month.setDate(1);
        month.setHours(0, 0, 0, 0);
        this._selectDate(month);
        return;
    }

    if (this._visibleMonthsHaveAvailableArrival()) {
        return;
    }

    let rangeStart = new Date(this.options.minDate.getTime());
    rangeStart.setHours(0, 0, 0, 0);

    let rangeEnd = this.options.maxDate ? new Date(this.options.maxDate.getTime()) : new Date(rangeStart.getTime());
    if (!this.options.maxDate) {
        rangeEnd.setFullYear(rangeEnd.getFullYear() + 2);
    }
    rangeEnd.setHours(0, 0, 0, 0);

    const firstAvailable = this._findFirstAvailableArrivalDate(rangeStart, rangeEnd);
    if (!firstAvailable) {
        return;
    }

    const month = new Date(firstAvailable.getFullYear(), firstAvailable.getMonth(), 1);
    this._selectDate(month);
}

DateRangePickerWrapper.prototype._getLockReason = function(date) {
    const currentDateRestriction = this.getRestriction(date);
    const isDepartureSelection = this._selection.date_from && !this._selection.date_to && date > this._selection.date_from;
    const isInSelectedRange = this._selection.date_from && this._selection.date_to && date > this._selection.date_from && date <= this._selection.date_to;

    // Для даты выезда разрешаем zero_quota, closed_arrival, closed и no_prices
    if (isDepartureSelection || isInSelectedRange) {
        // Проверяем, есть ли в диапазоне даты, недоступные для проживания
        if (isDepartureSelection) {
            let hasInvalidDates = false;
            let checkDate = new Date(this._selection.date_from);
            checkDate.setDate(checkDate.getDate() + 1);

            while (checkDate < date) {
                const restriction = this.getRestriction(checkDate);
                if (restriction && (restriction.zero_quota || restriction.no_prices || restriction.closed || restriction.closed_arrival)) {
                    hasInvalidDates = true;
                    break;
                }
                checkDate.setDate(checkDate.getDate() + 1);
            }

            if (hasInvalidDates) {
                return {
                    tooltip: 'В диапазоне есть недоступные даты',
                    status: LOCK_UNAVAILABLE,
                };
            }
        }

        // Разрешаем выбор даты выезда с ограничениями
        if (currentDateRestriction && (currentDateRestriction.zero_quota || currentDateRestriction.closed_arrival || currentDateRestriction.closed || currentDateRestriction.no_prices)) {
            return null;
        }
    }

    // забронировано (разрешаем для выезда)
    if (currentDateRestriction && currentDateRestriction.zero_quota) {
        return {
            tooltip: 'Забронировано',
            status: isDepartureSelection || isInSelectedRange ? null : LOCK_LOCKED,
        };
    }

    // нет цен (но разрешаем для выезда)
    if (currentDateRestriction && currentDateRestriction.no_prices) {
        return {
            tooltip: 'Не указаны цены',
            status: isDepartureSelection || isInSelectedRange ? null : LOCK_UNAVAILABLE,
        };
    }

    // закрыт (но разрешаем для выезда)
    if (currentDateRestriction && currentDateRestriction.closed) {
        return {
            tooltip: 'Закрыт',
            status: isDepartureSelection || isInSelectedRange ? null : LOCK_UNAVAILABLE,
        };
    }

    // запрет заезда (разрешаем для выезда)
    if (currentDateRestriction && currentDateRestriction.closed_arrival) {
        return {
            tooltip: 'Заезд недоступен',
            status: isDepartureSelection || isInSelectedRange ? null : LOCK_OVERLAP_AVAILABLE,
        };
    }

    // запрет выезда если дата выбирается в обратном порядке
    if (this._selection.date_from && !this._selection.date_to && date < this._selection.date_from) {
        const restriction = this.getRestriction(this._selection.date_from);
        if (restriction && restriction.closed_departure) {
            return {
                tooltip: 'Выезд недоступен',
                status: LOCK_OVERLAP_AVAILABLE,
            };
        }
    }

    // если выбраны обе даты, разрешаем выбор любых дат для изменения
    if (this._selection.date_from && this._selection.date_to) {
        return;
    }

    // обработка минимального/максимального срока проживания
    if (this._visualSelection.date_from && this._visualSelection.date_to) {
        let date_from = this._visualSelection.date_from;
        let date_to = this._visualSelection.date_to;
        if (date_from > date_to) {
            [date_from, date_to] = [date_to, date_from];
        }

        const restrictions = this._visualSelectionRestrictions.slice(0, -1);
        if (restrictions.length) {
            const arrivalRestriction = restrictions.find(restriction => restriction.date.getTime() == date_from.getTime());
            const minStayRestrictions = restrictions.filter(restriction => restriction.min_stay).map(restriction => restriction.min_stay);
            const maxStayRestrictions = restrictions.filter(restriction => restriction.max_stay).map(restriction => restriction.max_stay);

            if (arrivalRestriction && arrivalRestriction.min_stay_arrival) {
                minStayRestrictions.push(arrivalRestriction.min_stay_arrival);
            }

            if (minStayRestrictions.length) {
                const minStay = Math.max(...minStayRestrictions);
                const nights = Math.abs(this._visualSelection.date_from.getTime() - date.getTime()) / 86400 / 1000;

                if (nights < minStay && this._visualSelection.date_from.getTime() != date.getTime()) {
                    return {
                        tooltip: `Минимум ночей: ${minStay}`,
                        status: LOCK_OVERLAP_AVAILABLE,
                    };
                }
            }

            if (maxStayRestrictions.length) {
                const maxStay = Math.min(...maxStayRestrictions);
                const nights = Math.abs(this._visualSelection.date_from.getTime() - date.getTime()) / 86400 / 1000;

                if (nights > maxStay && this._visualSelection.date_from.getTime() != date.getTime()) {
                    return {
                        tooltip: `Максимум ночей: ${maxStay}`,
                        status: LOCK_UNAVAILABLE,
                    };
                }
            }
        }
    }

    // запрет выезда
    if (currentDateRestriction && currentDateRestriction.closed_departure && this._selection.date_from && this._selection.date_from.getTime() != date.getTime()) {
        return {
            tooltip: 'Выезд недоступен',
            status: LOCK_OVERLAP_AVAILABLE,
        };
    }
};

/**
 * Визуальное выделение элементов
 * @param {Date} date_from Начальная дата
 * @param {Date} date_to   Конечная дата
 */
DateRangePickerWrapper.prototype._rangeVisualSelect = function(date_from, date_to) {
    // выделение не изменилось
    if (date_from && date_to && this._visualSelection.date_from?.getTime() == date_from.getTime() && this._visualSelection.date_to?.getTime() == date_to.getTime()) {
        return;
    }

    this._visualSelectionRestrictions = [];

    if (date_from && date_to) {
        let correct_date_from = date_from;
        let correct_date_to = date_to;

        if (correct_date_from > correct_date_to) {
            [correct_date_from, correct_date_to] = [correct_date_to, correct_date_from];
        }

        this._visualSelectionRestrictions = this.getRestrictions(correct_date_from, correct_date_to);
    }

    DateRangePicker.prototype._rangeVisualSelect.call(this, date_from, date_to);

    this._$months.querySelectorAll('.Month').forEach($month => {
        $month.querySelectorAll('.Week').forEach($week => {
            const $selected = $week.querySelectorAll('.Day.is-selected, .Day.is-selected-between');
            let $wrapper = $week.querySelector('.Days__selected');

            if ($wrapper) {
                this._unwrapDaysSelected($wrapper);
            }

            if (!$selected.length) {
                return
            }

            $wrapper = this._$createElement('<div class="Days__selected"></div>');
            $week.insertBefore($wrapper, $selected[0]);

            $selected.forEach($day => {
                $wrapper.appendChild($day);
            });
        });
    });
}

/**
 * Сброс выделения
 */
DateRangePickerWrapper.prototype._rangeVisualReset = function() {
    this._visualSelectionRestrictions = [];

    DateRangePicker.prototype._rangeVisualReset.call(this);

    this._$months.querySelectorAll('.Days__selected').forEach(this._unwrapDaysSelected);
}

/**
 * Перенос выделенных дней из контейнера
 */
DateRangePickerWrapper.prototype._unwrapDaysSelected = function($wrapper) {
    while ($wrapper.firstElementChild) {
        $wrapper.parentElement.insertBefore($wrapper.firstElementChild, $wrapper);
    }

    $wrapper.parentElement.removeChild($wrapper);
}

/**
 * Вызов события по подписке
 */
export const triggerEvent = (event_name, ...data) => {
    window.triggerSubscribers = typeof window.triggerSubscribers === 'object' ? window.triggerSubscribers : [];

    // инициализируем
    window.triggerSubscribers[event_name] = window.triggerSubscribers[event_name] || {
        triggered: false,
        data: {},
        callbacks: []
    };

    // отмечаем вызов и сохраняем данные
    window.triggerSubscribers[event_name].triggered = true;
    window.triggerSubscribers[event_name].data = data;

    // вызываем всех
    let result;
    window.triggerSubscribers[event_name].callbacks.filter(callback => {
        return !callback.triggered;
    }).forEach(callback => {
        result = callback.callback.apply(null, data);

        // ставим true если нужно вызвать 1 раз
        callback.triggered = !callback.options.multiple;
    });

    return result;
}

export default DateRangePickerWrapper;
